import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors } from '../styles/styles';
import { reflectThoughts } from '../services/openAiService';

// Reflect: pour out a free-form message and get back the saved thoughts that
// speak to it. The server reads the whole corpus and returns real saved rows,
// so every result is one of your actual thoughts (tappable through to detail).
const formatDate = (ts) => {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const ReflectView = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  // Cancel any in-flight request when the page unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  const run = async () => {
    const text = message.trim();
    if (!text || loading) return;
    // Cancel a prior in-flight request (e.g. a same-tick double submit) so a
    // slow response can't overwrite a fresher one.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setResults(null);
    try {
      const thoughts = await reflectThoughts(text, { signal: controller.signal });
      if (!controller.signal.aborted) setResults(thoughts);
    } catch (e) {
      if (e?.name !== 'AbortError') {
        console.error('reflect failed:', e);
        if (!controller.signal.aborted) setResults([]);
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') run();
  };

  const disabled = loading || !message.trim();

  return (
    <div
      className="page-content"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        padding: '80px 16px 40px',
        maxWidth: '720px',
        margin: '0 auto',
        backgroundColor: colors.background,
        color: colors.textPrimary,
      }}
    >
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="What's on your mind right now?"
        rows={7}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          resize: 'vertical',
          padding: '14px',
          borderRadius: '12px',
          backgroundColor: colors.surfaceElevated,
          color: colors.textPrimary,
          border: `1px solid ${colors.divider}`,
          fontSize: '15px',
          lineHeight: 1.5,
          fontFamily: 'inherit',
          outline: 'none',
        }}
      />

      <button
        onClick={run}
        disabled={disabled}
        className="ios-button"
        style={{
          marginTop: '12px',
          alignSelf: 'flex-start',
          padding: '10px 20px',
          fontSize: '15px',
          fontWeight: 'bold',
          backgroundColor: 'transparent',
          color: colors.accent.white,
          border: `1px solid ${colors.accent.white}`,
          borderRadius: '20px',
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        {loading ? 'Reflecting…' : 'Reflect'}
      </button>

      <div style={{ marginTop: '28px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {results && results.length === 0 && !loading && (
          <p style={{ color: colors.textSecondary, fontSize: '14px' }}>
            No matching thoughts found.
          </p>
        )}
        {results && results.map((t, i) => (
          <div
            key={t.id || i}
            onClick={() => t.id && navigate(`/thought/${t.id}`)}
            className="ios-button"
            style={{
              padding: '14px 16px',
              borderRadius: '12px',
              backgroundColor: colors.surfaceElevated,
              border: `1px solid ${colors.divider}`,
              cursor: t.id ? 'pointer' : 'default',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: colors.textTertiary, fontSize: '12px' }}>{formatDate(t.ts)}</span>
              <span style={{ color: colors.textTertiary, fontSize: '12px' }}>#{i + 1}</span>
            </div>
            <div style={{ fontSize: '15px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{t.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReflectView;
